import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Plus, Trash2, FileText, PenTool, BookOpen, ExternalLink } from 'lucide-react';
import { LoadingState, LifecycleBadge } from '@systemdox/ui';
import {
  specsApi,
  type SpecDetail,
  type SpecRequirement,
  type SpecVersionDetail,
  type SpecPublishedCommit,
  type DocRepos,
} from '@/services/specsApi';
import { docsApi } from '@/services/docsApi';
import { PublishedBadge } from '@/components/specs/PublishedBadge';
import { PublishDialog } from '@/components/specs/PublishDialog';
import { useConsoleUrl } from '@/hooks/useConsoleUrl';
import { SpecLinksPanel } from '@/components/specs/SpecLinksPanel';
import { FullEditorShell, type FullEditorTab } from '@/components/editor/FullEditorShell';
import { DocContentEditor, type EditorMode } from '@/components/editor/DocContentEditor';
import type { EditorHistorySource } from '@/components/editor/VersionHistoryModal';
import { useAutosave } from '@/hooks/useAutosave';

type Tab = 'proposal' | 'requirements' | 'tasks';

/** Where each pillar's documents live — the breadcrumb's source page. */
const PILLAR_CRUMB = {
  requirement: { label: 'Requirements', icon: FileText, path: '/docs/requirements' },
  design: { label: 'Design', icon: PenTool, path: '/docs/design' },
  doc: { label: 'Docs', icon: BookOpen, path: '/docs' },
} as const;

/** One markdown document out of a checkpoint, for the history preview pane. */
function composeVersionMarkdown(v: SpecVersionDetail): string {
  const parts: string[] = [];
  if (v.proposalContent.trim()) parts.push(v.proposalContent);
  if (v.requirements.length > 0) {
    parts.push('---\n\n# Requirements');
    for (const req of v.requirements) {
      parts.push(`## ${req.name || 'Requirement'}\n\n${req.content}`);
    }
  }
  if (v.taskContent.trim()) parts.push(`---\n\n# Tasks\n\n${v.taskContent}`);
  return parts.join('\n\n') || '_This checkpoint is empty._';
}

/**
 * The consolidated full editor for Requirements and Design documents
 * (specs/changes/consolidated-full-editor). Opens in preview; Edit switches
 * every tab to the shared WYSIWYG — content autosaves while editing and
 * flushes on the way back to preview.
 */
export default function SpecEditorPage() {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const consoleUrl = useConsoleUrl();
  // Keep the query string on every in-app target — on localhost the ?tenant
  // param IS the host selection.
  const { search } = useLocation();
  const [spec, setSpec] = useState<SpecDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('proposal');
  const [mode, setMode] = useState<EditorMode>('preview');
  const [showPublish, setShowPublish] = useState(false);
  // The pillar's configured docs repo — the publish dialog's fallback target
  // for specs with no linked repo. This moved here with the dialog when the
  // list rows lost their Publish action (docs-pillar-parity R5): the dialog
  // must still open ready to go.
  const [docRepos, setDocRepos] = useState<DocRepos | null>(null);

  useEffect(() => {
    specsApi.getDocRepos().then(setDocRepos).catch(() => setDocRepos(null));
  }, []);

  // Editable state
  const [proposal, setProposal] = useState('');
  const [tasks, setTasks] = useState('');
  const [requirements, setRequirements] = useState<SpecRequirement[]>([]);

  useEffect(() => {
    if (!draftId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await specsApi.getSpec(draftId);
        if (cancelled) return;
        setSpec(data);
        setProposal(data.proposalContent);
        setTasks(data.taskContent);
        setRequirements(data.requirements);
        // Git-source (ADR-033): once committed, the content lives in git and is
        // edited in the reader — preview the CURRENT git content here, read-only,
        // never the Draft# copy (which stops being written after commit and would
        // otherwise show stale). A missing file just falls back to the Draft# copy.
        if (data.gitPath && data.gitRepo) {
          const [owner, repo] = data.gitRepo.split('/');
          const isFile = data.gitPath.endsWith('.md');
          const [prop, tsk] = await Promise.all([
            docsApi.getFileContent(owner, repo, isFile ? data.gitPath : `${data.gitPath}/proposal.md`).catch(() => null),
            isFile ? Promise.resolve(null)
              : docsApi.getFileContent(owner, repo, `${data.gitPath}/tasks.md`).catch(() => null),
          ]);
          if (cancelled) return;
          if (prop && !prop.error) setProposal(prop.content);
          if (tsk && !tsk.error) setTasks(tsk.content);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [draftId]);

  const handleSave = useCallback(async () => {
    if (!draftId) return;
    await specsApi.updateSpec(draftId, {
      proposalContent: proposal,
      taskContent: tasks,
      requirements,
    });
    // Keep the local record's clock honest so the published/draft-ahead
    // badge flips as soon as an edit lands (kb-publish R6).
    setSpec((prev) => (prev ? { ...prev, updatedAt: new Date().toISOString() } : prev));
  }, [draftId, proposal, tasks, requirements]);

  const { dirty, saving, touch, flush } = useAutosave(handleSave);

  // Leaving edit mode flushes whatever autosave hasn't caught up with yet.
  const handleModeChange = useCallback((next: EditorMode) => {
    setMode(next);
    if (next === 'preview') void flush();
  }, [flush]);

  const editProposal = useCallback((value: string) => {
    setProposal(value);
    touch();
  }, [touch]);

  const editTasks = useCallback((value: string) => {
    setTasks(value);
    touch();
  }, [touch]);

  const editRequirement = useCallback((index: number, patch: Partial<SpecRequirement>) => {
    setRequirements((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    touch();
  }, [touch]);

  const addRequirement = useCallback(() => {
    setRequirements((prev) => [
      ...prev,
      { reqId: '', name: 'New Requirement', content: '' },
    ]);
    setActiveTab('requirements');
    setMode('edit');
    touch();
  }, [touch]);

  const removeRequirement = useCallback((index: number) => {
    setRequirements((prev) => prev.filter((_, i) => i !== index));
    touch();
  }, [touch]);

  const pillar = PILLAR_CRUMB[spec?.docType ?? 'doc'] ?? PILLAR_CRUMB.doc;

  const handleArchive = useCallback(async () => {
    if (!draftId) return;
    await flush();
    await specsApi.archiveSpec(draftId);
    // Return to the pillar this document belongs to (#543).
    navigate({ pathname: pillar.path, search });
  }, [draftId, flush, navigate, pillar.path, search]);

  const handlePublish = useCallback(async () => {
    // Publish ships what's on screen — flush pending edits first.
    await flush();
    setShowPublish(true);
  }, [flush]);

  // Git-source (ADR-033): once committed, content lives in git and is edited in
  // the reader — so this page is read-only and its history is git's, not the
  // pre-commit Draft# checkpoints (restoring those wouldn't touch git).
  const committed = !!spec?.gitPath && !!spec?.gitRepo;

  // View History: drafting checkpoints (consolidated-full-editor R4) merged
  // with the document's published git history (kb-publish R6) — git is the
  // durable record of published states, checkpoints the working memory. For a
  // committed doc only the git commits are shown (see above).
  const history: EditorHistorySource = useMemo(() => {
    const commitsById = new Map<string, SpecPublishedCommit>();
    return {
      list: async () => {
        if (!draftId) return [];
        const [checkpoints, commits] = await Promise.all([
          committed ? Promise.resolve([]) : specsApi.listSpecVersions(draftId),
          specsApi.getPublishedHistory(draftId).catch(() => []),
        ]);
        commitsById.clear();
        const entries = [
          ...checkpoints.map((v) => ({
            id: v.versionId,
            savedAt: v.versionId,
            label: v.trigger === 'publish' ? 'Publish checkpoint' : 'Edit checkpoint',
            detail: `${v.requirementCount} requirement${v.requirementCount === 1 ? '' : 's'}`,
          })),
          ...commits.map((c) => {
            const id = `git:${c.fullSha}`;
            commitsById.set(id, c);
            return {
              id,
              savedAt: c.date,
              label: `Published · ${c.sha}`,
              detail: c.message,
              restorable: false,
            };
          }),
        ];
        return entries.sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
      },
      get: async (id) => {
        const commit = commitsById.get(id);
        if (commit) {
          return {
            id,
            content:
              `**Commit \`${commit.sha}\`** — ${commit.message}\n\n` +
              `${commit.author || 'unknown'} · ${commit.date ? new Date(commit.date).toLocaleString() : ''}\n\n` +
              (commit.htmlUrl ? `[View on GitHub](${commit.htmlUrl})\n\n` : '') +
              `_Published states live in git — this commit touched the document's docs/ folder._`,
          };
        }
        const version = await specsApi.getSpecVersion(draftId!, id);
        return { id, content: composeVersionMarkdown(version) };
      },
      restore: async (id) => {
        // Git-source docs are read-only here, and git entries never restore.
        if (committed || commitsById.has(id)) return;
        const version = await specsApi.getSpecVersion(draftId!, id);
        await specsApi.updateSpec(draftId!, {
          proposalContent: version.proposalContent,
          taskContent: version.taskContent,
          requirements: version.requirements,
          ...(version.name ? { name: version.name } : {}),
        });
        setProposal(version.proposalContent);
        setTasks(version.taskContent);
        setRequirements(version.requirements);
        if (version.name) setSpec((prev) => (prev ? { ...prev, name: version.name } : prev));
        setMode('preview');
      },
    };
  }, [draftId, committed]);

  if (loading) {
    return <LoadingState size="lg" />;
  }

  if (!spec || !draftId) {
    return <div className="p-8 text-slate-500">Spec not found</div>;
  }

  // `committed` is derived above (git-source) — a committed document is edited
  // in the reader, so this page is read-only and points there; only a repo-less
  // draft is still edited here.
  const gitIsFile = !!spec.gitPath?.endsWith('.md');
  const readerHref = (file: string) => {
    const path = gitIsFile ? spec.gitPath! : `${spec.gitPath}/${file}`;
    return `/book/${spec.gitRepo}/${path}${search}`;
  };
  const reqSlug = (name: string) => (name || 'untitled').toLowerCase().replace(/ /g, '-');

  const tabs: FullEditorTab[] = [
    { key: 'proposal', label: 'Proposal' },
    { key: 'requirements', label: `Requirements (${requirements.length})` },
    { key: 'tasks', label: 'Tasks' },
  ];

  const editInReader = committed ? (
    <Link
      to={readerHref('proposal.md')}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
    >
      <ExternalLink className="w-3.5 h-3.5" /> Edit in reader
    </Link>
  ) : null;

  return (
    <FullEditorShell
      crumbs={[
        { label: 'Docs', to: { pathname: '/docs', search } },
        { label: pillar.label, icon: pillar.icon, to: { pathname: pillar.path, search } },
      ]}
      title={spec.name}
      titleBadges={
        <>
          <LifecycleBadge status={spec.status} />
          <PublishedBadge
            publishedAt={spec.publishedAt}
            updatedAt={spec.updatedAt}
          />
        </>
      }
      mode={committed ? 'preview' : mode}
      onModeChange={handleModeChange}
      canEdit={!committed}
      dirty={dirty}
      saving={saving}
      onSave={() => void flush()}
      onPublish={() => void handlePublish()}
      history={history}
      onArchive={() => void handleArchive()}
      showArchive={spec.status !== 'archived'}
      extraActions={
        committed ? editInReader : (
          <button
            onClick={addRequirement}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" /> Add Requirement
          </button>
        )
      }
      belowHeader={
        <>
          {committed && (
            <div className="shrink-0 flex items-center gap-2 px-4 py-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-800">
              <BookOpen className="w-3.5 h-3.5 shrink-0" />
              <span>This document is versioned in git. Edit it in the reader — changes commit straight to GitHub.</span>
            </div>
          )}
          {(spec.docType === 'design' || spec.docType === 'requirement') && (
            // Traceability links: design → satisfies, requirement → satisfied by (#545)
            <div className="shrink-0">
              <SpecLinksPanel draftId={draftId} docType={spec.docType} />
            </div>
          )}
        </>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={(key) => setActiveTab(key as Tab)}
    >
      <div className="p-6 h-full">
        {activeTab === 'proposal' && (
          <div className="h-full flex flex-col">
            <DocContentEditor
              value={proposal}
              onChange={editProposal}
              mode={mode}
              placeholder="Write your proposal…"
              emptyHint="No proposal yet — click Edit to start writing."
              className="flex-1 min-h-[500px]"
            />
          </div>
        )}

        {activeTab === 'requirements' && committed && (
          <div className="space-y-2">
            {requirements.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-8">No requirements.</p>
            )}
            {requirements.map((req, index) => (
              <Link
                key={index}
                to={readerHref(`specs/${reqSlug(req.name)}.md`)}
                className="flex items-center gap-2 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/50 dark:hover:bg-blue-900/10"
              >
                <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-sm text-slate-900 dark:text-slate-100 flex-1 truncate">{req.name || 'Requirement'}</span>
                <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </Link>
            ))}
          </div>
        )}

        {activeTab === 'requirements' && !committed && (
          <div className="space-y-4">
            {requirements.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-8">
                No requirements yet. Click "Add Requirement" to create one.
              </p>
            )}
            {requirements.map((req, index) => (
              <div key={index} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  {mode === 'edit' ? (
                    <input
                      type="text"
                      value={req.name}
                      onChange={(e) => editRequirement(index, { name: e.target.value })}
                      className="bg-transparent text-sm font-medium text-slate-900 dark:text-slate-100 outline-none flex-1"
                      placeholder="Requirement name"
                    />
                  ) : (
                    <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 flex-1 truncate">
                      {req.name || 'Requirement'}
                    </h3>
                  )}
                  {req.reqId && (
                    <code className="text-[10px] font-mono text-slate-400 shrink-0">{req.reqId}</code>
                  )}
                  {mode === 'edit' && (
                    <button
                      onClick={() => removeRequirement(index)}
                      className="text-slate-400 hover:text-red-500 p-1 shrink-0"
                      title="Remove requirement"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <DocContentEditor
                  value={req.content}
                  onChange={(content) => editRequirement(index, { content })}
                  mode={mode}
                  placeholder="Write requirement with MUST/SHOULD/MAY keywords and Given/When/Then scenarios…"
                  emptyHint="No content yet — click Edit to write this requirement."
                  className="border-0 rounded-none min-h-[260px]"
                />
              </div>
            ))}
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="h-full flex flex-col">
            <DocContentEditor
              value={tasks}
              onChange={editTasks}
              mode={mode}
              placeholder="Write implementation tasks…"
              emptyHint="No tasks yet — click Edit to start writing."
              className="flex-1 min-h-[500px]"
            />
          </div>
        )}
      </div>

      {showPublish && (
        <PublishDialog
          draftId={spec.draftId}
          specName={spec.name}
          // The spec's own repo, else the pillar's configured docs repo —
          // the dialog opens ready to go (kb-publish; formerly the list's job).
          defaultRepo={spec.repoFullName || (spec.docType === 'design' ? docRepos?.designRepo : docRepos?.requirementsRepo) || ''}
          configureHref={consoleUrl ? consoleUrl('/settings/repo-roles') : undefined}
          onClose={() => setShowPublish(false)}
          onPublished={() => {
            // publishedAt/status change server-side — re-read rather than
            // guessing at the row shape.
            specsApi.getSpec(spec.draftId).then(setSpec).catch(() => {});
          }}
          onUnpublished={() => {
            specsApi.getSpec(spec.draftId).then(setSpec).catch(() => {});
          }}
        />
      )}
    </FullEditorShell>
  );
}
